// Create a credit note (BUILD_SPEC.md §8.4 step 5: "Credit notes reference
// invoices, optional restock"). Plan.md G23. Reached either from an invoice
// detail's "Create credit note" button (which passes `?invoiceId=`) or directly
// from the credit-notes register, in which case the user picks a creditable
// (APPROVED/SYNCED/PAID) invoice here.
//
// NOTE on product/qty/price entry: `invoice_lines` (packages/core/src/schemas/
// invoicing.ts's `invoiceLineResponseSchema`) carries only a free-text
// `description` — not a `productId` — because a raised invoice line's product is
// baked into that description text (services/api/src/modules/invoicing/raise.ts's
// `buildLineDescription`). A credit-note line, by contrast, DOES require an
// explicit `productId` (it books a RETURN_IN stock movement when restocked), so
// this form cannot auto-fill credit lines from the invoice's own lines — the
// invoice's lines are shown read-only for reference, and the user picks the
// product/qty/price to credit explicitly.
import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { IconAlertCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { VAT_TREATMENTS, type VatTreatment } from '@zebra/core';
import { ApiRequestError } from '../../api/client';
import { useCanSell } from '../../auth/permissions';
import { ProductSelect } from './components/ProductSelect';
import { AnyLocationSelect } from './components/AnyLocationSelect';
import { CustomerName } from './components/EntityNames';
import { formatMoneyCents, formatQty, randToCents } from './format';
import { useCreateCreditNote, useCreditableInvoices, useInvoice, type CreditNoteLineWriteInput } from './api';

interface CreditLineFormRow {
  key: string;
  productId: string;
  productLabel: string;
  description: string;
  qty: string;
  priceRand: string;
  vatTreatment: VatTreatment;
  locationId: string;
}

function emptyCreditLine(): CreditLineFormRow {
  return { key: crypto.randomUUID(), productId: '', productLabel: '', description: '', qty: '', priceRand: '', vatTreatment: 'STANDARD', locationId: '' };
}

export function CreditNoteNew({ preselectedInvoiceId }: { preselectedInvoiceId?: string }) {
  const navigate = useNavigate();
  // Client-side role gate (audit fix 2026-07-07): every sibling posting screen
  // disables its action for the wrong role with an explanatory tooltip
  // (auth/permissions.ts convention — defense-in-depth/UX only; the server's
  // `requireRole('SALES_ACCOUNTS', 'ADMIN')` on POST /invoicing/credit-notes
  // remains the source of truth).
  const canSell = useCanSell();
  const { data: candidates } = useCreditableInvoices();
  const [invoiceId, setInvoiceId] = useState(preselectedInvoiceId ?? '');
  const invoice = useInvoice(invoiceId || undefined);
  const [reason, setReason] = useState('');
  const [restock, setRestock] = useState(false);
  const [lines, setLines] = useState<CreditLineFormRow[]>([emptyCreditLine()]);
  const createCreditNote = useCreateCreditNote();

  useEffect(() => {
    if (preselectedInvoiceId) setInvoiceId(preselectedInvoiceId);
  }, [preselectedInvoiceId]);

  const creditableOptions = (candidates?.items ?? [])
    .filter((inv) => inv.status === 'APPROVED' || inv.status === 'SYNCED' || inv.status === 'PAID')
    .map((inv) => ({ value: inv.id, label: `${inv.number} — ${formatMoneyCents(inv.totalCents)}` }));

  function updateLine(key: string, patch: Partial<CreditLineFormRow>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }
  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l.key !== key));
  }
  function addLine() {
    setLines((prev) => [...prev, emptyCreditLine()]);
  }

  const linesValid =
    lines.length > 0 &&
    lines.every(
      (l) =>
        l.productId &&
        l.description.trim() !== '' &&
        l.qty &&
        Number(l.qty) > 0 &&
        l.priceRand.trim() !== '' &&
        (!restock || l.locationId),
    );
  const canSubmit = Boolean(invoiceId) && linesValid;

  async function handleSubmit() {
    const lineInputs: CreditNoteLineWriteInput[] = lines.map((l) => ({
      productId: l.productId,
      locationId: restock ? l.locationId : null,
      description: l.description.trim(),
      qty: l.qty,
      unitPriceCents: randToCents(l.priceRand),
      vatTreatment: l.vatTreatment,
    }));
    const created = await createCreditNote.mutateAsync({
      invoiceId,
      reason: reason.trim() || null,
      restock,
      lines: lineInputs,
    });
    void navigate({ to: '/sales-invoicing/credit-notes/$creditNoteId', params: { creditNoteId: created.id } });
  }

  return (
    <Stack gap="lg" maw={900}>
      <Group justify="space-between">
        <Title order={2}>New credit note</Title>
        <Button component={Link} to="/sales-invoicing/credit-notes" variant="subtle">
          Back to register
        </Button>
      </Group>

      {createCreditNote.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {createCreditNote.error instanceof ApiRequestError ? createCreditNote.error.message : 'Failed to create the credit note.'}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="md">
          <Select
            label="Invoice to credit"
            description="Only approved, synced or paid invoices can be credited — draft invoices aren't listed."
            required
            searchable
            disabled={Boolean(preselectedInvoiceId)}
            data={creditableOptions}
            value={invoiceId || null}
            onChange={(v) => setInvoiceId(v ?? '')}
            placeholder="Search by invoice number…"
            comboboxProps={{ withinPortal: true }}
          />
          {invoice.isLoading && <Loader size="sm" />}
          {invoice.data && (
            <Stack gap={4}>
              <Group gap={6}>
                <Text fw={600}>Customer:</Text>
                <CustomerName id={invoice.data.customerId} />
              </Group>
              <Table.ScrollContainer minWidth={640}>
                <Table verticalSpacing={4}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Invoice line</Table.Th>
                      <Table.Th>Qty</Table.Th>
                      <Table.Th ta="right">Line total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {invoice.data.lines.map((l) => (
                      <Table.Tr key={l.id}>
                        <Table.Td>{l.description}</Table.Td>
                        <Table.Td>{formatQty(l.qty)}</Table.Td>
                        <Table.Td ta="right">{formatMoneyCents(l.lineTotalCents)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Stack>
          )}
          <Textarea label="Reason (optional)" value={reason} onChange={(e) => setReason(e.currentTarget.value)} autosize minRows={2} />
          <Switch
            label="Restock — book a RETURN_IN movement for each line"
            description="When on, the credited goods are added back into stock at the location you choose per line. When off, this credit note only refunds money — nothing moves in the ledger."
            checked={restock}
            onChange={(e) => setRestock(e.currentTarget.checked)}
          />
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Table.ScrollContainer minWidth={800}>
            <Table verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th w={110}>
                    <Tooltip label="In the product's base unit — kg or count, depending on the product chosen." multiline w={220}>
                      <span>Qty</span>
                    </Tooltip>
                  </Table.Th>
                  <Table.Th w={130}>Unit price (R, ex VAT)</Table.Th>
                  <Table.Th w={130}>VAT</Table.Th>
                  {restock && <Table.Th>Restock location</Table.Th>}
                  <Table.Th w={48} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lines.map((line) => (
                  <Table.Tr key={line.key}>
                    <Table.Td>
                      <ProductSelect value={line.productId} onChange={(id, label) => updateLine(line.key, { productId: id, productLabel: label, description: line.description || label })} aria-label="Product" />
                    </Table.Td>
                    <Table.Td>
                      <TextInput value={line.description} onChange={(e) => updateLine(line.key, { description: e.currentTarget.value })} aria-label="Description" />
                    </Table.Td>
                    <Table.Td>
                      <TextInput value={line.qty} onChange={(e) => updateLine(line.key, { qty: e.currentTarget.value })} aria-label="Quantity" placeholder="0.000" />
                    </Table.Td>
                    <Table.Td>
                      <TextInput value={line.priceRand} onChange={(e) => updateLine(line.key, { priceRand: e.currentTarget.value })} aria-label="Unit price" placeholder="0.00" />
                    </Table.Td>
                    <Table.Td>
                      <Select
                        aria-label="VAT treatment"
                        data={VAT_TREATMENTS.map((v) => ({ value: v, label: v }))}
                        value={line.vatTreatment}
                        onChange={(v) => updateLine(line.key, { vatTreatment: (v as VatTreatment) ?? 'STANDARD' })}
                        comboboxProps={{ withinPortal: true }}
                      />
                    </Table.Td>
                    {restock && (
                      <Table.Td>
                        <AnyLocationSelect value={line.locationId} onChange={(id) => updateLine(line.key, { locationId: id })} aria-label="Restock location" />
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Tooltip label="Remove this line from the credit note">
                        <ActionIcon color="red" variant="subtle" aria-label="Remove line" onClick={() => removeLine(line.key)} disabled={lines.length <= 1}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Group justify="flex-end">
            <Button variant="light" leftSection={<IconPlus size={16} />} onClick={addLine}>
              Add line
            </Button>
          </Group>
        </Stack>
      </Paper>

      <Group justify="flex-end">
        <Tooltip label="Requires Sales/Accounts role" disabled={canSell}>
          <span>
            <Button size="lg" disabled={!canSell || !canSubmit} loading={createCreditNote.isPending} onClick={handleSubmit}>
              Create credit note
            </Button>
          </span>
        </Tooltip>
      </Group>
    </Stack>
  );
}

// Search-param schema for arriving from an invoice's "Create credit note" button.
// EXPORTED per this goal's S0 instruction ("EXPORT any route `validateSearch`
// interface — unexported -> TS4023").
export interface CreditNoteNewSearch {
  invoiceId?: string;
}
